import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { X, Loader2, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { inventoryApi } from "./inventoryApi";
import { DISPOSITIONS } from "./procurementConstants";

export const ReceiveDialog = ({ open, onClose, po, vendors, vehicles, currentUser, onReceived }) => {
  const [vendorId, setVendorId] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [tax, setTax] = useState(0);
  const [freight, setFreight] = useState(0);
  const [invoiceUpload, setInvoiceUpload] = useState(null);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Unknown";

  useEffect(() => {
    if (open) {
      setVendorId(po?.vendor_id || "");
      setInvoiceNumber(""); setTax(0); setFreight(0); setInvoiceUpload(null);
      if (po) {
        setLines(po.line_items.map((l) => ({
          po_line_id: l.id, part_id: l.part_id, part_number: l.part_number, description: l.description,
          quantity: Math.max(0, (l.quantity || 0) - (l.qty_received || 0)),
          unit_cost: l.unit_cost || 0, core_charge: l.core_charge || 0, core_required: (l.core_charge || 0) > 0,
          disposition: "add_to_inventory", qty_to_inventory: 0, qty_immediate: 0,
          unit_id: "", unit_number: "", unit_type: "",
        })));
      } else {
        setLines([{ part_id: "", part_number: "", description: "", quantity: 1, unit_cost: 0, core_charge: 0, core_required: false, disposition: "add_to_inventory", unit_id: "", unit_number: "", unit_type: "" }]);
      }
    }
  }, [open, po]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)));

  const submit = async () => {
    if (!vendorId) { toast.error("Select a vendor"); return; }
    const toReceive = lines.filter((l) => Number(l.quantity) > 0);
    if (toReceive.length === 0) { toast.error("Nothing to receive"); return; }
    setSaving(true);
    try {
      const res = await inventoryApi.receiveParts({
        po_id: po?.id || null, vendor_id: vendorId, invoice_number: invoiceNumber,
        received_date: new Date().toISOString(), invoice_upload: invoiceUpload,
        tax: Number(tax) || 0, freight: Number(freight) || 0,
        lines: toReceive.map((l) => {
          const unit = vehicles.find((v) => v.id === l.unit_id);
          return {
            po_line_id: l.po_line_id || null, part_id: l.part_id || null, part_number: l.part_number || null,
            description: l.description, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost) || 0,
            core_charge: Number(l.core_charge) || 0, core_required: !!l.core_required, disposition: l.disposition,
            qty_to_inventory: l.disposition === "split" ? Number(l.qty_to_inventory) || 0 : null,
            qty_immediate: l.disposition === "split" ? Number(l.qty_immediate) || 0 : null,
            unit_id: l.unit_id || null, unit_number: unit?.unit_number || null, unit_type: unit?.vehicle_type || null,
          };
        }),
        received_by: userName,
      });
      toast.success(`Received · ${res.cores_created} core(s) created`);
      onReceived(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to receive"); }
    setSaving(false);
  };

  const handleInvoice = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const reader = new FileReader(); reader.onload = (ev) => setInvoiceUpload(ev.target.result); reader.readAsDataURL(f);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col" hideClose data-testid="receive-dialog">
        <DialogTitle className="sr-only">Receive Parts</DialogTitle>
        <DialogDescription className="sr-only">Receive parts against a purchase order</DialogDescription>
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-900">Receive Parts {po ? `· ${po.po_number}` : ""}</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5 text-slate-500" /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500 mb-1 block">Vendor</Label>
              <select className="w-full h-10 px-3 rounded-md border border-slate-200 text-sm" value={vendorId} onChange={(e) => setVendorId(e.target.value)} disabled={!!po} data-testid="receive-vendor-select">
                <option value="">Select vendor...</option>
                {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select>
            </div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Invoice Number</Label><Input value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} data-testid="receive-invoice-input" /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Tax ($)</Label><Input type="number" step="0.01" value={tax} onChange={(e) => setTax(e.target.value)} /></div>
            <div><Label className="text-xs text-slate-500 mb-1 block">Freight ($)</Label><Input type="number" step="0.01" value={freight} onChange={(e) => setFreight(e.target.value)} /></div>
          </div>

          <div className="space-y-3">
            {lines.map((l, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3 space-y-2" data-testid={`receive-line-${i}`}>
                <div className="font-medium text-sm text-slate-800">{l.description} {l.part_number ? <span className="text-xs font-mono text-slate-400">{l.part_number}</span> : null}</div>
                <div className="grid grid-cols-3 gap-2">
                  <div><Label className="text-[10px] text-slate-400">Qty</Label><Input type="number" value={l.quantity} onChange={(e) => setLine(i, "quantity", e.target.value)} data-testid={`receive-qty-${i}`} /></div>
                  <div><Label className="text-[10px] text-slate-400">Unit Cost</Label><Input type="number" step="0.01" value={l.unit_cost} onChange={(e) => setLine(i, "unit_cost", e.target.value)} /></div>
                  <div><Label className="text-[10px] text-slate-400">Core Charge</Label><Input type="number" step="0.01" value={l.core_charge} onChange={(e) => setLine(i, "core_charge", e.target.value)} /></div>
                </div>
                <div><Label className="text-[10px] text-slate-400">Disposition</Label>
                  <select className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm" value={l.disposition} onChange={(e) => setLine(i, "disposition", e.target.value)} data-testid={`receive-disp-${i}`}>
                    {DISPOSITIONS.map((d) => <option key={d.value} value={d.value}>{d.label}</option>)}
                  </select>
                </div>
                {l.disposition === "split" && (
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] text-slate-400">Qty to Inventory</Label><Input type="number" value={l.qty_to_inventory} onChange={(e) => setLine(i, "qty_to_inventory", e.target.value)} /></div>
                    <div><Label className="text-[10px] text-slate-400">Qty Immediate Use</Label><Input type="number" value={l.qty_immediate} onChange={(e) => setLine(i, "qty_immediate", e.target.value)} /></div>
                  </div>
                )}
                {(l.disposition === "use_immediately" || l.disposition === "split") && (
                  <div><Label className="text-[10px] text-slate-400">Install on Unit</Label>
                    <select className="w-full h-9 px-2 rounded-md border border-slate-200 text-sm" value={l.unit_id} onChange={(e) => setLine(i, "unit_id", e.target.value)}>
                      <option value="">Select unit...</option>
                      {vehicles.map((v) => <option key={v.id} value={v.id}>Unit {v.unit_number}</option>)}
                    </select>
                  </div>
                )}
              </div>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm text-blue-600 cursor-pointer">
            <Upload className="w-4 h-4" /> {invoiceUpload ? "Invoice attached" : "Attach invoice (optional)"}
            <input type="file" accept="image/*,application/pdf" className="hidden" onChange={handleInvoice} />
          </label>
        </div>

        <div className="flex gap-3 px-5 py-4 border-t border-slate-200 flex-shrink-0">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button className="flex-1" onClick={submit} disabled={saving} data-testid="receive-submit">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Receive"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
